/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import { default as fragments } from '../../fragments'

import {
  Spinner,
  SvgIcon,
  Table,
  Warning
} from '../'

import {
  decodeBase64,
} from '../../utils'

import {
  ApplicationDetailsRow
} from './ApplicationDetailsRow'

const mergeApplications = (loanApplications, creditApplications) => {
  if (loanApplications.length > 0 && creditApplications.length > 0) {
    return [...loanApplications, ...creditApplications]
  } else if (loanApplications.length > 0) {
    return loanApplications
  } else if (creditApplications.length > 0) {
    return creditApplications
  } else {
    return []
  }
}

class ClientLoanAndCreditApplications extends React.Component {
  static propTypes = {
    employerId: PropTypes.string,
    divisionId: PropTypes.string,
    status: PropTypes.string,
    loanType: PropTypes.string,
    createdAtStartDate: PropTypes.string,
    createdAtEndDate: PropTypes.string,
    clientId: PropTypes.string.isRequired,
    search: PropTypes.string,
    orderBy: PropTypes.string.isRequired,
    changeSort: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = {
    filterChanged: false,
  }

  goToDetailPage = (loanAppId, startCursor) => {
    this.props.history.push(`/os/products/credit/applications/${loanAppId}`)
  }

  render() {
    let { data, condensedView, clientId, className } = this.props

    if (data.loading) {
      return (
        <div className='slds-m-top_xx-large'>
          <Spinner type='tail' className='slds-m-top_x-large' />
          <div className='slds-align_absolute-center u-font-size-larger'>
            Fetching data...
          </div>
        </div>
      )
    }

    if (data.error) {
      return <Warning>Error loading loan application data.</Warning>
    }

    let { loanapplications, creditapplications } = data
    let applications = mergeApplications(loanapplications.edges, creditapplications.edges)

    if (applications.length === 0) {
      return (
        <div className='slds-m-around_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_small'
          />
          No loan applications found.
        </div>
      )
    }

    let tableColumns = condensedView  // NOTE: at the moment no distinction
      ? ['Name', 'Cash', 'Retail', 'Consolidation', 'Emergency', 'Status', 'Opened', 'Assigned to']
      : clientId
        ? ['Cash', 'Retail', 'Consolidation', 'Emergency', 'Status', 'Opened', 'Assigned to']
        : ['Name', 'Cash', 'Retail', 'Consolidation', 'Emergency', 'Status', 'Opened', 'Assigned to']

    return (
      <div className={className}>
        <Table
          columns={tableColumns}
          rows={applications.map((loanApp, index) => (
            <ApplicationDetailsRow
              loanApp={loanApp.node}
              onClick={(loanApplicationId, isCreditApplicationNode) => this.props.onClick
                ? this.props.onClick(loanApplicationId)
                : this.goToDetailPage(loanApplicationId, null)
              }
              condensedView={this.props.condensedView}
              key={`loanDetailsRow_${index}`}
            />
          ))}
          objectRows={true}
          orderBy={this.props.orderBy}
          changeSort={this.props.changeSort}
          querySortableColumns={{
            'Name': { orderByName: 'client__identity__first_name' },
            'Opened': { orderByName: 'created_at' },
            'Assigned to': { orderByName: 'assigned_to__first_name' }
          }}
        />
      </div>
    )
  }
}

const getApplications = gql`
  query (
    $employer: UUID
    $division: UUID
    $status: String
    $creditApplicationStatus: String
    $assignedTo: UUID
    $cashLoan: Decimal
    $retailLoan: Decimal
    $consolidationLoan: Decimal
    $createdAtStartDate: DateTime
    $createdAtEndDate: DateTime
    $clientId: UUID
    $search: String
    $orderBy: String
  ) {
    loanapplications(
      client_Division_Employer_Id: $employer
      client_Division_Id: $division
      status: $status
      assignedTo_Id: $assignedTo
      cashAppliedFor_Gt: $cashLoan
      retailAppliedFor_Gt: $retailLoan
      consolidationAppliedFor_Gt: $consolidationLoan
      createdOnOrAfter: $createdAtStartDate
      createdOnOrBefore: $createdAtEndDate
      client_Id: $clientId
      search: $search
      orderBy: $orderBy
    ) {
      edges {
        node {
          ...LoanApplicationListing
        }
      }
    }
    creditapplications(
      client_Division_Employer_Id: $employer
      client_Division_Id: $division
      status: $creditApplicationStatus
      assignedTo_Id: $assignedTo
      createdOnOrAfter: $createdAtStartDate
      createdOnOrBefore: $createdAtEndDate
      client_Id: $clientId
      search: $search
      orderBy: $orderBy
    ) {
      edges {
        node {
          ...CreditApplicationListing
        }
      }
    }
  }
  ${fragments.loanApplicationListing}
  ${fragments.creditApplicationListing}
`;

const withData =
  graphql(getApplications, {
    options: ({ employerId, divisionId, status, search, assignedTo, loanType, createdAtStartDate, createdAtEndDate, clientId, orderBy }) => ({
      variables: {
        employer: employerId ? decodeBase64(employerId) : undefined,
        division: divisionId ? decodeBase64(divisionId) : undefined,
        status,
        creditApplicationStatus: status === 'loans_pending_disbursements'
          ? 'credits_pending_disbursements'
          : status,
        assignedTo,
        cashLoan: loanType === 'cash' ? 0 : null,
        retailLoan: loanType === 'retail_cash' ? 0 : null,
        consolidationLoan: loanType === 'consolidation' ? 0 : null,
        createdAtStartDate,
        createdAtEndDate,
        search,
        clientId: clientId ? decodeBase64(clientId) : undefined,
        orderBy
      },
      fetchPolicy: 'network-only'
    })
  })

export default compose(
  withData,
  withRouter
)(ClientLoanAndCreditApplications)



// WEBPACK FOOTER //
// ./src/components/client-loan-and-credit-applications/index.js